/**
=========================================================
* Trust Cargo - hero slider
=========================================================

* Three slides on an eight-second rotation, with dots and arrows. Pauses
* while the pointer is over it, and stops entirely if the visitor has asked
* their system for reduced motion.
*
* The scenery is an inline SVG port at dusk - cranes, a container ship and a
* stack of containers. Nothing is downloaded, so the fold paints immediately
* even on a slow connection.
*/

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import { useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { C, goldButton, ghostButton } from "./ui";

const ROTATE_MS = 8000;

/** The port skyline behind the words. */
function PortScene() {
  return (
    <SuiBox
      aria-hidden
      sx={{
        position: "absolute",
        insetInline: 0,
        bottom: 0,
        height: { xs: "42%", md: "58%" },
        zIndex: 1,
        opacity: 0.9,
        pointerEvents: "none",
      }}
    >
      <svg viewBox="0 0 1440 420" preserveAspectRatio="xMidYMax slice" width="100%" height="100%">
        <defs>
          <linearGradient id="tcSea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#12264D" />
            <stop offset="100%" stopColor="#060F22" />
          </linearGradient>
          <linearGradient id="tcGold" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#C9A227" />
            <stop offset="100%" stopColor="#E4C766" />
          </linearGradient>
        </defs>

        {/* water */}
        <rect x="0" y="250" width="1440" height="170" fill="url(#tcSea)" />

        {/* far cranes */}
        <g stroke="rgba(201,162,39,0.35)" strokeWidth="3" fill="none">
          {[120, 300, 480].map((x) => (
            <g key={x} transform={`translate(${x} 0)`}>
              <path d="M0 250 L0 120 L110 120" />
              <path d="M0 150 L70 120" />
              <path d="M75 120 L75 165" />
              <rect
                x="62"
                y="165"
                width="26"
                height="18"
                fill="rgba(201,162,39,0.25)"
                stroke="none"
              />
            </g>
          ))}
        </g>

        {/* container ship */}
        <g transform="translate(700 128)">
          <g fill="rgba(201,162,39,0.5)">
            {[0, 46, 92, 138, 184, 230].map((x, row) =>
              [0, 26, 52]
                .slice(0, row % 2 === 0 ? 3 : 2)
                .map((y) => (
                  <rect key={`${x}-${y}`} x={x} y={100 - y} width="40" height="20" rx="2" />
                ))
            )}
          </g>
          <path d="M-20 122 L300 122 L268 168 L14 168 Z" fill="url(#tcGold)" opacity="0.85" />
          <rect x="250" y="76" width="34" height="46" rx="3" fill="rgba(247,244,238,0.75)" />
        </g>

        {/* quayside container stack */}
        <g transform="translate(1080 176)">
          {[0, 1, 2].map((r) =>
            [0, 1, 2, 3]
              .slice(0, 4 - r)
              .map((c) => (
                <rect
                  key={`${r}-${c}`}
                  x={c * 62}
                  y={74 - r * 26}
                  width="56"
                  height="22"
                  rx="2"
                  fill={r === 1 && c === 1 ? "url(#tcGold)" : "rgba(201,162,39,0.32)"}
                />
              ))
          )}
        </g>

        {/* waterline shimmer */}
        <g stroke="rgba(228,199,102,0.25)" strokeWidth="2" strokeLinecap="round">
          {[280, 320, 360, 400].map((y, i) => (
            <path key={y} d={`M${60 + i * 40} ${y} h${180 - i * 22}`} />
          ))}
        </g>
      </svg>
    </SuiBox>
  );
}

function Hero({ t, onCta }) {
  const [i, setI] = useState(0);
  const paused = useRef(false);
  const navigate = useNavigate();
  const { slides } = t;

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) return undefined;

    const id = setInterval(() => {
      if (!paused.current) setI((n) => (n + 1) % slides.length);
    }, ROTATE_MS);

    return () => clearInterval(id);
  }, [slides.length]);

  const step = (d) => setI((n) => (n + d + slides.length) % slides.length);

  return (
    <SuiBox
      id="top"
      component="section"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      sx={{
        position: "relative",
        minHeight: { xs: "92vh", md: "100vh" },
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        background: `
          radial-gradient(1100px 620px at 15% 12%, rgba(201,162,39,0.20), transparent 60%),
          radial-gradient(900px 520px at 85% 10%, rgba(23,80,150,0.42), transparent 62%),
          linear-gradient(160deg, ${C.navy} 0%, ${C.navyDeep} 100%)
        `,
      }}
    >
      <PortScene />

      <SuiBox
        sx={{
          position: "relative",
          zIndex: 3,
          maxWidth: 1220,
          mx: "auto",
          width: "100%",
          px: { xs: 2.5, md: 5 },
          pt: { xs: 16, md: 14 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <SuiBox sx={{ maxWidth: 760 }}>
          {slides.map((s, n) => (
            <SuiBox
              key={s.title}
              sx={{
                display: n === i ? "block" : "none",
                animation: "tcFade 700ms cubic-bezier(.2,.7,.3,1)",
                "@keyframes tcFade": {
                  from: { opacity: 0, transform: "translateY(22px)" },
                  to: { opacity: 1, transform: "none" },
                },
              }}
            >
              {/* the kicker sits in a gold pill so the eye lands there first */}
              <SuiBox
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.9,
                  mb: 2.5,
                  borderRadius: 999,
                  border: `1px solid ${C.line}`,
                  background: "rgba(201,162,39,0.10)",
                }}
              >
                <SuiBox
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: C.gold,
                    boxShadow: `0 0 0 4px rgba(201,162,39,0.18)`,
                  }}
                />
                <SuiTypography sx={{ fontSize: 13.5, fontWeight: 700, color: C.goldLight }}>
                  {s.kicker}
                </SuiTypography>
              </SuiBox>

              <SuiTypography
                sx={{
                  fontSize: { xs: 34, sm: 44, md: 58 },
                  fontWeight: 800,
                  lineHeight: 1.18,
                  color: C.white,
                  mb: 2.5,
                  textShadow: "0 6px 30px rgba(0,0,0,0.35)",
                }}
              >
                {s.title}
              </SuiTypography>

              <SuiTypography
                sx={{
                  fontSize: { xs: 15.5, md: 18.5 },
                  lineHeight: 2,
                  color: C.muted,
                  mb: 4,
                  maxWidth: 640,
                }}
              >
                {s.text}
              </SuiTypography>

              <SuiBox sx={{ display: "flex", gap: 1.75, flexWrap: "wrap" }}>
                <SuiBox
                  component="button"
                  type="button"
                  sx={goldButton}
                  onClick={() => navigate("/authentication/sign-in")}
                >
                  <Icon sx={{ fontSize: "20px !important" }}>travel_explore</Icon>
                  {s.cta}
                </SuiBox>

                <SuiBox component="button" type="button" sx={ghostButton} onClick={onCta}>
                  {s.cta2}
                </SuiBox>
              </SuiBox>
            </SuiBox>
          ))}
        </SuiBox>

        {/* dots and arrows */}
        <SuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: { xs: 5, md: 7 },
          }}
        >
          <SuiBox sx={{ display: "flex", gap: 1.25 }}>
            {slides.map((s, n) => (
              <SuiBox
                key={s.title}
                component="button"
                type="button"
                aria-label={`Slide ${n + 1}`}
                onClick={() => setI(n)}
                sx={{
                  cursor: "pointer",
                  height: 6,
                  width: n === i ? 42 : 16,
                  border: "none",
                  padding: 0,
                  borderRadius: 999,
                  background:
                    n === i
                      ? `linear-gradient(90deg, ${C.goldLight}, ${C.gold})`
                      : "rgba(247,244,238,0.28)",
                  transition: "width 300ms ease, background-color 300ms ease",
                }}
              />
            ))}
          </SuiBox>

          <SuiBox sx={{ display: "flex", gap: 1 }}>
            {[
              { icon: "chevron_left", d: -1 },
              { icon: "chevron_right", d: 1 },
            ].map((b) => (
              <SuiBox
                key={b.icon}
                component="button"
                type="button"
                aria-label={b.icon}
                onClick={() => step(b.d)}
                sx={{
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  color: C.ivory,
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid ${C.line}`,
                  "&:hover": { background: "rgba(201,162,39,0.18)", borderColor: C.gold },
                }}
              >
                <Icon>{b.icon}</Icon>
              </SuiBox>
            ))}
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
}

Hero.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  t: PropTypes.object.isRequired,
  onCta: PropTypes.func.isRequired,
};

export default Hero;
